import React, { useEffect } from 'react';
import type { Form, FormTheme } from '../../types/form';
import { resolveFormTheme } from '../../types/form';
import { FormFieldRenderer } from './FormFieldRenderer';
import { getSectionFields, sortSections } from './formNavigation';

interface FormThemePreviewProps {
  form: Form;
  theme?: FormTheme | null;
}

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Lato:wght@400;700&family=Roboto:wght@400;500;700&display=swap';

export function ensureFormThemeFontsLoaded(): void {
  if (typeof document === 'undefined')
  {
    return;
  }

  const existing = document.querySelector(`link[data-form-theme-fonts="true"]`);
  if (existing)
  {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  link.setAttribute('data-form-theme-fonts', 'true');
  document.head.appendChild(link);
}

export function buildFormThemeCssVars(theme: FormTheme): React.CSSProperties {
  const resolved = resolveFormTheme(theme);
  return {
    ['--form-primary' as string]: resolved.primaryColor,
    ['--form-bg' as string]: resolved.backgroundColor,
    ['--form-font' as string]: `'${resolved.fontFamily}', sans-serif`,
    ['--form-title-size' as string]: `${resolved.textSizes.title}px`,
    ['--form-section-size' as string]: `${resolved.textSizes.section}px`,
    ['--form-question-size' as string]: `${resolved.textSizes.question}px`,
    ['--form-body-size' as string]: `${resolved.textSizes.body}px`,
    backgroundColor: resolved.backgroundColor,
    fontFamily: `'${resolved.fontFamily}', sans-serif`,
  };
}

export const FormThemePreview: React.FC<FormThemePreviewProps> = ({
  form,
  theme,
}) => {
  const resolved = resolveFormTheme(theme ?? form.theme);
  const sections = sortSections(form.sections);
  const firstSection = sections[0];
  const fields = firstSection
    ? getSectionFields(form.fields, firstSection.id).slice(0, 3)
    : form.fields.slice().sort((a, b) => a.order - b.order).slice(0, 3);

  useEffect(() => {
    ensureFormThemeFontsLoaded();
  }, []);

  return (
    <div
      className="min-h-[28rem] rounded-lg border border-gray-200"
      style={buildFormThemeCssVars(resolved)}
    >
      <header
        className="border-b text-white shadow-sm"
        style={{
          backgroundColor: resolved.primaryColor,
          borderColor: resolved.primaryColor,
        }}
      >
        <div className="mx-auto flex h-12 max-w-3xl items-center gap-2 px-4">
          <img className="h-6 w-auto" src="/know-logo.png" alt="" />
          <span className="text-sm font-semibold">Prévia do formulário</span>
        </div>
      </header>

      <div className="px-4 py-6">
        <div className="mx-auto w-full max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {resolved.headerImageUrl ? (
            <img
              src={resolved.headerImageUrl}
              alt=""
              className="h-40 w-full object-cover"
            />
          ) : null}

          <div className="space-y-2 border-b border-gray-100 px-6 py-5">
            <div
              className="mb-2 h-1.5 w-14 rounded-full"
              style={{ backgroundColor: resolved.primaryColor }}
            />
            <h1
              className="font-semibold text-gray-900"
              style={{ fontSize: resolved.textSizes.title }}
            >
              {form.title || 'Formulário sem título'}
            </h1>
            {form.description ? (
              <p
                className="text-gray-600"
                style={{ fontSize: resolved.textSizes.body }}
              >
                {form.description}
              </p>
            ) : null}
          </div>

          {firstSection && sections.length > 1 ? (
            <div className="border-b border-gray-100 px-6 py-4">
              <p
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: resolved.primaryColor }}
              >
                Seção 1 de {sections.length}
              </p>
              <h2
                className="mt-1 font-semibold text-gray-900"
                style={{ fontSize: resolved.textSizes.section }}
              >
                {firstSection.title}
              </h2>
            </div>
          ) : null}

          <div className="space-y-6 px-6 py-6">
            {fields.length === 0 ? (
              <p
                className="text-gray-500"
                style={{ fontSize: resolved.textSizes.body }}
              >
                Adicione perguntas na aba Perguntas para visualizar a prévia.
              </p>
            ) : (
              fields.map((field) => (
                <div
                  key={field.id}
                  className="border-t border-gray-100 pt-6 first:border-t-0 first:pt-0"
                  style={{ fontSize: resolved.textSizes.question }}
                >
                  <FormFieldRenderer
                    field={field}
                    value=""
                    onChange={() => undefined}
                  />
                </div>
              ))
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
            <button
              type="button"
              className="rounded-md px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: resolved.primaryColor }}
              disabled
            >
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormThemePreview;
